import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Plus, HardDrive, Server, Lock, LockOpen, RefreshCw } from 'lucide-react';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import PATH_PAGE from '@/router/path';
import { useListVolumes } from '@/service/gpu-instance/volume-hook';
import type { Volume, VolumeFilterOptions } from '@/service/gpu-instance/volume-type';
import { VolumeStatus } from '@/service/gpu-instance/volume-type';
import {
  VolumeStatusBadge,
  VolumeHealthIndicator,
  VolumeSearch,
  VolumeActionsDropdown,
  CreateVolumeDialog,
  ResizeVolumeDialog,
  AttachVolumeDialog,
  DeleteVolumeDialog,
  CloneVolumeDialog,
  CreateSnapshotDialog,
} from '../components/volume';

const VolumeListPage = () => {
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { withDemoPrefix } = useDemoPath();

  // Filter state
  const [filters, setFilters] = useState<VolumeFilterOptions>({});
  const [selectedVolumes, setSelectedVolumes] = useState<Set<string>>(new Set());

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resizeVolume, setResizeVolume] = useState<Volume | null>(null);
  const [attachVolume, setAttachVolume] = useState<Volume | null>(null);
  const [deleteVolume, setDeleteVolume] = useState<Volume | null>(null);
  const [cloneVolume, setCloneVolume] = useState<Volume | null>(null);
  const [snapshotVolume, setSnapshotVolume] = useState<Volume | null>(null);

  // Data fetching
  const { data: volumes = [], isLoading, refetch } = useListVolumes();

  // Filter volumes
  const filteredVolumes = useMemo(() => {
    return volumes.filter((volume) => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (!volume.name.toLowerCase().includes(search)) return false;
      }
      if (filters.status && filters.status.length > 0) {
        if (!filters.status.includes(volume.status)) return false;
      }
      if (filters.storageClassTier && filters.storageClassTier.length > 0) {
        if (!filters.storageClassTier.includes(volume.storageClass.tier)) return false;
      }
      if (filters.accessMode && filters.accessMode.length > 0) {
        if (!filters.accessMode.includes(volume.accessMode)) return false;
      }
      if (filters.healthStatus && filters.healthStatus.length > 0) {
        if (!filters.healthStatus.includes(volume.health.status)) return false;
      }
      if (filters.isAttached !== undefined) {
        if (volume.isAttached !== filters.isAttached) return false;
      }
      return true;
    });
  }, [volumes, filters]);

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedVolumes.size === filteredVolumes.length) {
      setSelectedVolumes(new Set());
    } else {
      setSelectedVolumes(new Set(filteredVolumes.map((v) => v.id)));
    }
  };

  const toggleSelectVolume = (volumeId: string) => {
    const newSelection = new Set(selectedVolumes);
    if (newSelection.has(volumeId)) {
      newSelection.delete(volumeId);
    } else {
      newSelection.add(volumeId);
    }
    setSelectedVolumes(newSelection);
  };

  // Navigate to volume detail
  const handleViewVolume = (volume: Volume) => {
    const path = PATH_PAGE.dashboard.gpuInstance.volumeById(orgId, workspaceId, volume.id).overview;
    navigate(withDemoPrefix(path));
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get access mode display
  const getAccessModeDisplay = (mode: string) => {
    const displays: Record<string, string> = {
      ReadWriteOnce: 'RWO',
      ReadWriteMany: 'RWX',
      ReadOnlyMany: 'ROX',
      ReadWriteOncePod: 'RWOP',
    };
    return displays[mode] || mode;
  };

  // Calculate totals
  const totalSize = volumes.reduce((sum, v) => sum + v.requestedSize, 0);
  const totalUsed = volumes.reduce((sum, v) => sum + v.usedSize, 0);

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Volume Management</h1>
          <p className="text-muted-foreground">
            Manage persistent storage volumes backed by NetApp Trident
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Volume
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Volumes</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{volumes.length}</div>
            <p className="text-xs text-muted-foreground">
              {volumes.filter((v) => v.isAttached).length} attached
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Storage</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSize.toLocaleString()} GB</div>
            <p className="text-xs text-muted-foreground">
              {totalUsed.toLocaleString()} GB used ({Math.round((totalUsed / totalSize) * 100) || 0}%)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Health Status</CardTitle>
            <span className="h-2 w-2 rounded-full bg-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {volumes.filter((v) => v.health.status === 'Healthy').length}/{volumes.length}
            </div>
            <p className="text-xs text-muted-foreground">volumes healthy</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <VolumeSearch
        filters={filters}
        onFiltersChange={setFilters}
        onSearch={(search) => setFilters((f) => ({ ...f, search }))}
      />

      {/* Volume Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Volumes
              {selectedVolumes.size > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selectedVolumes.size} selected
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {filteredVolumes.length} volume{filteredVolumes.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredVolumes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <HardDrive className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">No volumes found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {volumes.length === 0
                  ? 'Create your first volume to get started'
                  : 'No volumes match your current filters'}
              </p>
              {volumes.length === 0 && (
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Volume
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        filteredVolumes.length > 0 &&
                        selectedVolumes.size === filteredVolumes.length
                      }
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead>Storage Class</TableHead>
                  <TableHead>Instance</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVolumes.map((volume) => (
                  <TableRow
                    key={volume.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleViewVolume(volume)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedVolumes.has(volume.id)}
                        onCheckedChange={() => toggleSelectVolume(volume.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{volume.name}</span>
                        {volume.encrypted ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Lock className="h-3 w-3 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>Encrypted</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <LockOpen className="h-3 w-3 text-muted-foreground/50" />
                              </TooltipTrigger>
                              <TooltipContent>Not encrypted</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="w-32 space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>{volume.usedSize} GB</span>
                          <span className="text-muted-foreground">{volume.requestedSize} GB</span>
                        </div>
                        <Progress
                          value={(volume.usedSize / volume.requestedSize) * 100}
                          className="h-1.5"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <VolumeStatusBadge status={volume.status} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {getAccessModeDisplay(volume.accessMode)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{volume.storageClass.displayName}</span>
                    </TableCell>
                    <TableCell>
                      {volume.isAttached ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="secondary" className="text-xs">
                                {volume.attachedInstances.length} instance
                                {volume.attachedInstances.length !== 1 ? 's' : ''}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <ul className="text-xs">
                                {volume.attachedInstances.map((inst) => (
                                  <li key={inst.instanceId}>{inst.instanceName}</li>
                                ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not attached</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <VolumeHealthIndicator health={volume.health} showLabel={false} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(volume.createdAt)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <VolumeActionsDropdown
                        volume={volume}
                        onView={() => handleViewVolume(volume)}
                        onResize={() => setResizeVolume(volume)}
                        onAttach={() => setAttachVolume(volume)}
                        onDetach={() => {}}
                        onClone={() => setCloneVolume(volume)}
                        onSnapshot={() => setSnapshotVolume(volume)}
                        onDelete={() => setDeleteVolume(volume)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CreateVolumeDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => refetch()}
      />
      <ResizeVolumeDialog
        open={!!resizeVolume}
        onOpenChange={(open) => !open && setResizeVolume(null)}
        volume={resizeVolume}
        onSuccess={() => refetch()}
      />
      <AttachVolumeDialog
        open={!!attachVolume}
        onOpenChange={(open) => !open && setAttachVolume(null)}
        volume={attachVolume}
        onSuccess={() => refetch()}
      />
      <DeleteVolumeDialog
        open={!!deleteVolume}
        onOpenChange={(open) => !open && setDeleteVolume(null)}
        volume={deleteVolume}
        onSuccess={() => refetch()}
      />
      <CloneVolumeDialog
        open={!!cloneVolume}
        onOpenChange={(open) => !open && setCloneVolume(null)}
        volume={cloneVolume}
        onSuccess={() => refetch()}
      />
      <CreateSnapshotDialog
        open={!!snapshotVolume}
        onOpenChange={(open) => !open && setSnapshotVolume(null)}
        volume={snapshotVolume}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default VolumeListPage;
